import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, LayoutDashboard, ShieldCheck, Sparkles, Utensils } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import ShareAppButton from "@/components/ShareAppButton";
import InstallAppButton from "@/components/InstallAppButton";
import StudentNotifications from "@/components/StudentNotifications";
import OwnerNotifications from "@/components/OwnerNotifications";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const dashHref = user?.role === "admin" ? "/admin" : user?.role === "owner" ? "/owner" : "/dashboard";

  return (
    <>
      {/* Vibrant Rajasthani ribbon — first thing every visitor sees */}
      <div
        data-testid="top-ribbon"
        className="relative overflow-hidden bg-peacock-gradient text-white text-[11px] sm:text-xs"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-2 sm:gap-6 flex-wrap font-semibold tracking-wide">
          <span className="flex items-center gap-1.5">
            <Sparkles size={12} className="text-turmeric-300" />
            Beds from <b className="text-turmeric-300">₹250</b> / night
          </span>
          <span className="hidden sm:inline text-white/30">·</span>
          <span className="flex items-center gap-1.5">
            <Utensils size={12} className="text-turmeric-300" />
            Monthly <b className="text-turmeric-300">₹7,000</b> · lunch + dinner included
          </span>
          <span className="hidden md:inline text-white/30">·</span>
          <span className="hidden md:inline">Verified Stays. No Hidden Charges. Ever.</span>
        </div>
        {/* Rajasthani jharokha scallop bottom edge */}
        <div className="jharokha-border h-2 w-full" aria-hidden />
      </div>

      <header className="sticky top-0 z-40 border-b border-marigold-200 backdrop-blur-xl bg-white/85 relative">
        {/* subtle bandhani dot texture behind navbar */}
        <div className="bandhani-bg absolute inset-0 pointer-events-none" aria-hidden />
        <div className="relative max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          <Link to="/" data-testid="navbar-logo" className="group">
            <LogoLockup size={36} />
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <Link to="/hostels" data-testid="nav-browse" className="nav-link hover:text-vermilion-600 transition-colors">Browse</Link>
            <Link to="/kota" className="nav-link hover:text-vermilion-600 transition-colors">Kota</Link>
            <Link to="/jaipur" className="nav-link hover:text-vermilion-600 transition-colors">Jaipur</Link>
            <Link to="/sikar" className="nav-link hover:text-vermilion-600 transition-colors">Sikar</Link>
            <Link to="/delhi" className="nav-link hover:text-vermilion-600 transition-colors">Delhi</Link>
            <Link to="/jodhpur" className="nav-link hover:text-vermilion-600 transition-colors">Jodhpur</Link>
            <Link to="/#how" className="nav-link hover:text-vermilion-600 transition-colors">How</Link>
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="hidden md:flex items-center gap-2">
              <InstallAppButton />
              <ShareAppButton />
            </div>
            {user && user.role === "student" && <StudentNotifications />}
            {user && (user.role === "owner" || user.role === "admin") && <OwnerNotifications />}
            {!user ? (
              <>
                <Button data-testid="nav-login-btn" variant="ghost" className="hidden sm:inline-flex rounded-full h-10 px-4" onClick={() => nav("/login")}>Log in</Button>
                <Button data-testid="nav-signup-btn" className="btn-pop rounded-full h-9 px-3 sm:h-10 sm:px-5 bg-vermilion-600 hover:bg-vermilion-700 text-white text-sm whitespace-nowrap shadow-md shadow-vermilion-600/25" onClick={() => nav("/signup")}>Get started</Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="nav-user-menu" variant="outline" className="rounded-full gap-2 border-marigold-300 hover:bg-marigold-50">
                    <User size={16} /> {user.name.split(" ")[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs">
                    <div className="font-semibold">{user.name}</div>
                    <div className="text-slate-500 font-normal">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav(dashHref)}><LayoutDashboard size={16} className="mr-2" /> Dashboard</DropdownMenuItem>
                  {user.role === "student" && (
                    <DropdownMenuItem onClick={() => nav("/kyc")}><ShieldCheck size={16} className="mr-2" /> KYC</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="nav-logout" onClick={() => { logout(); nav("/"); }}>
                    <LogOut size={16} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
